import jsPDF from 'jspdf';
import 'svg2pdf.js';
import { markingApi } from '../api/marking';
import { extractHri } from './dataMatrix';
import type { MarkingCode } from '../api/marking';

/** Заменяет недопустимые для имени файла символы на '_'. */
function sanitizeFilename(s: string): string {
  return s
    .replace(/[<>:"/\\|?*\x00-\x1f]/g, '_')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 200);
}

/** Формирует имя файла: "YYYY-MM-DD_HH-MM-SS_<бренд название>_<тара>.pdf". */
export function markFilename(scan: MarkingCode): string {
  const dt = new Date(scan.scanned_at);
  const pad = (n: number) => String(n).padStart(2, '0');
  const date = `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
  const time = `${pad(dt.getHours())}-${pad(dt.getMinutes())}-${pad(dt.getSeconds())}`;
  const brand = (scan.brand || '').trim();
  const name = (scan.product_name || 'scan').trim();
  const size = scan.container_size ? `_${scan.container_size}` : '';
  const namePart = brand ? `${brand} ${name}` : name;
  return sanitizeFilename(`${date}_${time}_${namePart}${size}`) + '.pdf';
}

/* ===== Вспомогательные функции для вёрстки PDF ===== */

const MM_PER_INCH = 25.4;
const CANVAS_DPI = 150; // Разрешение offscreen-canvas для текста

const mmToPx = (mm: number) => Math.round((mm * CANVAS_DPI) / MM_PER_INCH);
const pxToMm = (px: number) => (px * MM_PER_INCH) / CANVAS_DPI;
const ptToPx = (pt: number) => Math.round((pt * CANVAS_DPI) / 72);

/**
 * Разбивает текст на строки, умещающиеся в maxWidth. Использует
 * реальное измерение через pdf.getTextWidth — сначала пробует
 * splitTextToSize (по словам), затем режет оставшиеся длинные
 * фрагменты посимвольно. Нужно для кодов без пробелов.
 */
function chunkTextByWidth(pdf: jsPDF, text: string, maxWidth: number): string[] {
  if (!text) return [];
  const bySplit = pdf.splitTextToSize(text, maxWidth) as string[];
  const out: string[] = [];
  for (const line of bySplit) {
    if (pdf.getTextWidth(line) <= maxWidth) {
      out.push(line);
      continue;
    }
    let current = '';
    for (const ch of line) {
      if (current && pdf.getTextWidth(current + ch) > maxWidth) {
        out.push(current);
        current = ch;
      } else {
        current += ch;
      }
    }
    if (current) out.push(current);
  }
  return out;
}

/**
 * Рендерит многострочный текст на offscreen-canvas — поддерживает
 * любой Unicode (включая кириллицу), в отличие от встроенных шрифтов
 * jsPDF. Возвращает canvas и его высоту в мм. Ширина canvas = widthMm.
 */
function renderTextCanvas(
  text: string,
  widthMm: number,
  fontSizePt: number,
  fontFamily = 'Arial, Helvetica, sans-serif',
): { canvas: HTMLCanvasElement; heightMm: number } {
  const widthPx = mmToPx(widthMm);
  const fontSizePx = ptToPx(fontSizePt);
  const lineHeightPx = Math.round(fontSizePx * 1.3);

  // Сначала считаем число строк на временном canvas
  const tmp = document.createElement('canvas');
  tmp.width = widthPx;
  tmp.height = 1;
  const tctx = tmp.getContext('2d')!;
  tctx.font = `${fontSizePx}px ${fontFamily}`;

  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    const test = line ? line + ' ' + word : word;
    if (line && tctx.measureText(test).width > widthPx) {
      lines.push(line);
      line = word;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  if (lines.length === 0) lines.push('');

  const heightPx = lines.length * lineHeightPx;

  const canvas = document.createElement('canvas');
  canvas.width = widthPx;
  canvas.height = heightPx;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, widthPx, heightPx);
  ctx.font = `${fontSizePx}px ${fontFamily}`;
  ctx.fillStyle = '#000';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  lines.forEach((l, i) => {
    ctx.fillText(l, widthPx / 2, i * lineHeightPx);
  });

  return { canvas, heightMm: pxToMm(heightPx) };
}

/* ===== Главная функция ===== */

/**
 * Генерирует PDF с одной маркой: A4 портрет.
 * Вёрстка (сверху вниз, всё по центру горизонтали):
 *   — Data Matrix 60×60 мм;
 *   — HRI-текст: (01)GTIN(21)serial (courier 10pt — то, что обычно печатают под маркой ЧЗ);
 *   — «Бренд Название, тара» (Arial 12pt, через canvas для корректной кириллицы).
 * Блок центрируется по вертикали листа.
 */
export async function generateMarkPdf(scan: MarkingCode): Promise<Blob> {
  const svgText = await markingApi.generateDataMatrixSvgText(scan.raw_code, 6);
  const svgDoc = new DOMParser().parseFromString(svgText, 'image/svg+xml');
  const svgElement = svgDoc.documentElement;
  if (svgElement.tagName.toLowerCase() !== 'svg' || svgElement.querySelector('parsererror')) {
    throw new Error('Не удалось распарсить SVG DataMatrix');
  }

  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = pdf.internal.pageSize.getWidth();   // 210
  const pageHeight = pdf.internal.pageSize.getHeight(); // 297

  const dmSize = 60;
  // Текст ограничен шириной самой марки
  const textMaxWidth = dmSize;
  const centerX = pageWidth / 2;
  const gapDmCode = 1;   // код вплотную к марке
  const gapCodeTitle = 1; // название впритык к коду

  // HRI — только GTIN + серийный номер (без криптохвоста)
  const hri = extractHri(scan.raw_code)
    || scan.raw_code.replace(/\x1d/g, ' | ');

  // Подпись «Бренд Название, тара»
  const brand = (scan.brand || '').trim();
  const name = (scan.product_name || '').trim();
  const size = scan.container_size ? `, ${scan.container_size}` : '';
  const namePart = brand && name ? `${brand} ${name}` : (name || brand);
  const title = (namePart + size).trim();

  // HRI: разбиваем с учётом реальной ширины строки — крупнее и жирным
  pdf.setFont('courier', 'bold');
  pdf.setFontSize(11);
  const codeLineHeight = 4.2;
  const codeLines = chunkTextByWidth(pdf, hri, textMaxWidth);
  const codeBlockHeight = codeLines.length * codeLineHeight;

  // Подпись: рендерим через canvas (поддержка Unicode). Мелко, не жирным.
  const titleRender = title
    ? renderTextCanvas(title, textMaxWidth, 7)
    : null;
  const titleHeight = titleRender?.heightMm ?? 0;

  const totalHeight = dmSize + gapDmCode + codeBlockHeight
    + (titleRender ? gapCodeTitle + titleHeight : 0);
  const startY = Math.max(20, (pageHeight - totalHeight) / 2);

  // DM
  await pdf.svg(svgElement, {
    x: (pageWidth - dmSize) / 2,
    y: startY,
    width: dmSize,
    height: dmSize,
  });

  // HRI
  pdf.setFont('courier', 'bold');
  pdf.setFontSize(11);
  let cursorY = startY + dmSize + gapDmCode;
  for (const line of codeLines) {
    pdf.text(line, centerX, cursorY, { align: 'center', baseline: 'top' });
    cursorY += codeLineHeight;
  }

  // Подпись (как изображение)
  if (titleRender) {
    cursorY = startY + dmSize + gapDmCode + codeBlockHeight + gapCodeTitle;
    pdf.addImage(titleRender.canvas.toDataURL('image/png'), 'PNG',
      (pageWidth - textMaxWidth) / 2, cursorY, textMaxWidth, titleHeight);
  }

  return pdf.output('blob');
}

/**
 * Сохраняет PDF-файлы для каждого скана.
 *
 * На Chromium-браузерах (Chrome, Edge) использует File System Access API —
 * пользователь выбирает папку и все файлы пишутся туда.
 * На остальных (Firefox, Safari) — каждый файл скачивается в папку загрузок.
 *
 * Возвращает число сохранённых файлов. Бросает ошибку при сбое.
 * При отмене диалога выбора папки возвращает 0 (не ошибка).
 */
export async function saveMarksToFolder(scans: MarkingCode[]): Promise<number> {
  if (scans.length === 0) return 0;

  const picker = (window as any).showDirectoryPicker;
  if (typeof picker === 'function') {
    let dirHandle: any;
    try {
      dirHandle = await picker({ id: 'marking-marks', mode: 'readwrite' });
    } catch (e: any) {
      if (e?.name === 'AbortError') return 0;
      throw e;
    }

    let saved = 0;
    for (const scan of scans) {
      const blob = await generateMarkPdf(scan);
      const filename = markFilename(scan);
      const fileHandle = await dirHandle.getFileHandle(filename, { create: true });
      const writable = await fileHandle.createWritable();
      await writable.write(blob);
      await writable.close();
      saved++;
    }
    return saved;
  }

  // Fallback: скачиваем каждый файл по отдельности
  for (const scan of scans) {
    const blob = await generateMarkPdf(scan);
    const filename = markFilename(scan);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    await new Promise((r) => setTimeout(r, 50));
  }
  return scans.length;
}
